import type { InfiniteData } from '@tanstack/react-query';
import type {
  DocumentContentsPageParam,
  DocumentFile,
  DocumentFolder,
  FolderContentsResponse,
} from '../types';

export type InfiniteFolderContentsData = InfiniteData<
  FolderContentsResponse,
  DocumentContentsPageParam
>;

function normalizeSearch(search?: string) {
  return search?.trim().toLocaleLowerCase() ?? '';
}

export function folderMatchesSearch(folderName: string, search?: string) {
  const normalizedSearch = normalizeSearch(search);
  if (!normalizedSearch) {
    return true;
  }

  return folderName.toLocaleLowerCase().includes(normalizedSearch);
}

export function fileMatchesSearch(fileName: string, search?: string) {
  const normalizedSearch = normalizeSearch(search);
  if (!normalizedSearch) {
    return true;
  }

  return fileName.toLocaleLowerCase().includes(normalizedSearch);
}

export function prependFolderToInfiniteData(
  data: InfiniteFolderContentsData | undefined,
  folder: DocumentFolder,
  search?: string
): InfiniteFolderContentsData | undefined {
  if (!data || !folderMatchesSearch(folder.name, search)) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map((page, index) =>
      index === 0
        ? {
            ...page,
            folders: [
              folder,
              ...page.folders.filter(
                (existingFolder) => existingFolder.id !== folder.id
              ),
            ],
          }
        : page
    ),
  };
}

export function renameFolderInInfiniteData(
  data: InfiniteFolderContentsData | undefined,
  folderId: string,
  nextName: string,
  search?: string
): InfiniteFolderContentsData | undefined {
  if (!data) {
    return data;
  }

  const shouldRemainVisible = folderMatchesSearch(nextName, search);

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      folders: shouldRemainVisible
        ? page.folders.map((folder) =>
            folder.id === folderId
              ? {
                  ...folder,
                  name: nextName,
                }
              : folder
          )
        : page.folders.filter((folder) => folder.id !== folderId),
      currentFolder:
        page.currentFolder?.id === folderId
          ? {
              ...page.currentFolder,
              name: nextName,
            }
          : page.currentFolder,
    })),
  };
}

export function removeFolderFromInfiniteData(
  data: InfiniteFolderContentsData | undefined,
  folderId: string
): InfiniteFolderContentsData | undefined {
  if (!data) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      folders: page.folders.filter((folder) => folder.id !== folderId),
    })),
  };
}

export function appendFileToInfiniteData(
  data: InfiniteFolderContentsData | undefined,
  file: DocumentFile,
  search?: string
): InfiniteFolderContentsData | undefined {
  if (!data || !fileMatchesSearch(file.name, search)) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map((page, index) =>
      index === 0
        ? {
            ...page,
            files: [
              ...page.files.filter((existingFile) => existingFile.id !== file.id),
              file,
            ],
          }
        : page
    ),
  };
}

export function updateFileInInfiniteData(
  data: InfiniteFolderContentsData | undefined,
  fileId: string,
  updater: (file: DocumentFile) => DocumentFile
): InfiniteFolderContentsData | undefined {
  if (!data) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      files: page.files.map((file) =>
        file.id === fileId ? updater(file) : file
      ),
    })),
  };
}
